import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';

// Utils
import { snackActions } from 'Utils/notificationsController';

// Constants
import { getAppUrl, getSecuritizeUrl } from 'Utils/urls';
import { PROJECT, SECURITIZE_ISSUER_ID } from 'Constants/index';

// Types
import {
  AccountType,
  GetUserDetailsDocument,
  useLinkOrgSecuritizeMutation,
  useLinkUserSecuritizeMutation,
} from 'Graphql/schema-v2';

const appUrl = getAppUrl(PROJECT);
const securitizeUrl = getSecuritizeUrl(PROJECT);
const redirectUrl = `${appUrl}/#/lend/dashboard`;
const encodedRedirectURI = encodeURIComponent(redirectUrl);

const SecuritizeRedirect = () => {
  const location = useLocation();
  const [linkUserSecuritize] = useLinkUserSecuritizeMutation();
  const [linkOrgSecuritize] = useLinkOrgSecuritizeMutation();

  useEffect(() => {
    const performLinkUserSecuritize = async () => {
      const queryParams = new URLSearchParams(location.search);
      const code = queryParams.get('code');
      const accountId = queryParams.get('accountId');
      const accountType = queryParams.get('accountType');

      if (code && accountType && accountId) {
        // TODO: it is incorrect to query by accountId, because it is set as org.id if user is org.
        const refetchQueries = [{ query: GetUserDetailsDocument, variables: { id: accountId } }];

        try {
          let response: any;

          switch (accountType) {
            case AccountType.Individual:
              response = await linkUserSecuritize({ variables: { code, userId: accountId }, refetchQueries });
              break;
            case AccountType.LegalEntity:
              response = await linkOrgSecuritize({ variables: { code, orgId: accountId }, refetchQueries });
              break;
            default:
              break;
          }

          const isSuccessful =
            response?.data?.linkOrgSecuritize?.securitizeId || response?.data?.linkUserSecuritize?.securitizeId;

          if (isSuccessful) {
            const kycUrl = `${securitizeUrl}/profile/verification/type?issuerId=${SECURITIZE_ISSUER_ID}&scope=info details verification&redirecturl=${encodedRedirectURI}`;

            window.open(kycUrl, '_self');

            snackActions.toast({
              title: 'Successfully linked Securitize account.',
              message: 'Redirecting to verification step...',
              variant: 'success',
            });
          }
        } catch (e: any) {
          console.error(`Error linking ${accountType} with Securitize:`, e);
          snackActions.toast({
            title: 'Failed to link Securitize account',
            message: e.message,
            variant: 'error',
          });
        }
      }
    };

    performLinkUserSecuritize();
  }, [location, linkUserSecuritize]);

  return <LinearProgress />;
};

export default SecuritizeRedirect;
